import { Badge, Card, Progress, Space, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { progressFactory } from "../../helpers/progressFactory"
import { EnrolmentActionButton } from "../EnrolmentActionButton"
import { TagFactory } from "../TagFactory"

// This card is used only on the My Learning page.
export function MyLearningCard({ enrolment, onMouseEnter }) {
  const { t } = useTranslation()
  const { publication } = enrolment
  const type = publication.type
  const overviewPath = `/${type}s/${enrolment.uid}`
  const isNew = dayjs().diff(enrolment.createdAt, "days") <= 12 && enrolment.currentState === "not-started"
  const topics = [...(publication.topics || [])]
  if (isNew) topics.unshift("New")
  const rawProgress =
    type === "program"
      ? progressFactory.program(enrolment).resultAsPercent
      : progressFactory.course(enrolment).resultAsPercent
  const progress = Number.isFinite(rawProgress) ? Math.max(0, Math.min(100, rawProgress)) : 0
  const meta =
    type === "program"
      ? `${publication.materialCount} Sections`
      : `${publication.materialCount} ${publication.materialCount > 1 ? t("modules") : t("module")}`

  return (
    <Card className="myLearningCard" onMouseEnter={onMouseEnter}>
      <Link
        className="myLearningCard__overview"
        to={overviewPath}
        aria-label={`${publication.content?.title || "Untitled"} overview`}
      />
      <div
        className="myLearningCard__image"
        style={{
          backgroundImage: publication.featureImageUrl
            ? `url('${publication.featureImageUrl}')`
            : "linear-gradient(to right, var(--ant-primary-color), var(--ant-primary-color-active))",
        }}
      />
      <div className="myLearningCard__content">
        <Space className="myLearningCard__topics" wrap>
          {topics.slice(0, 2).map((topic, index) => (
            <TagFactory key={`${typeof topic === "string" ? topic : topic.value}-${index}`}>
              {typeof topic === "string" ? topic : topic.value}
            </TagFactory>
          ))}
          {topics.length > 2 && <span className="badge--meta">+ {topics.length - 2}</span>}
        </Space>
        <Typography.Title level={4} ellipsis={{ rows: 2 }}>
          {publication.content?.title || "No Title Given"}
        </Typography.Title>
        {publication.content?.summary && (
          <Typography.Paragraph type="secondary" className="ellipses--two">
            {publication.content.summary}
          </Typography.Paragraph>
        )}
        <Space size={16} wrap>
          {type !== "program" && (
            <Badge
              color="green"
              text={
                <span className="meta">
                  {enrolment.dueDate ? dayjs(enrolment.dueDate).format("DD/MM/YYYY") : t("noDueDate")}
                </span>
              }
            />
          )}
          <Badge color="purple" text={<span className="meta">{meta}</span>} />
        </Space>
        <div className="myLearningCard__footer">
          <Progress percent={progress} size="small" />
          <div className="myLearningCard__action">
            {enrolment.isLocked ? (
              <>
                <Typography.Text>This {type} requires the following to be completed first:</Typography.Text>
                <Link
                  className="ant-btn ant-btn-primary"
                  to={`/${enrolment.preRequisite?.type}s/${enrolment.preRequisite?.publicationEnrolmentUid}`}
                >
                  {enrolment.preRequisite?.title}
                </Link>
              </>
            ) : type === "external" ? (
              <Link
                className="ant-btn ant-btn-primary"
                to={enrolment.currentState === "in-progress" ? `/externals/${enrolment.uid}/learning` : overviewPath}
              >
                {enrolment.currentState === "in-progress" ? t("resumeCourse") : t("goToExternal")}
              </Link>
            ) : (
              <EnrolmentActionButton enrolment={enrolment} />
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
